import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
    MultipleChoiceRenderer,
    OpenTextRenderer,
    MultipleSelectRenderer,
    MatrixRenderer,
    DragAndDropRenderer,
    DynamicTextPreview,
    type UserInteractionMetaDTO,
    defaultOptions,
    type QuestionRendererOptions,
} from '@/src/lib';

import { apiRequest } from '@/src/services';
import type {
    QtiStimulusQuestionDTO,
    QtiQuestionDTO,
    QtiMultipleChoiceQuestionDTO,
    QtiOpenTextQuestionDTO,
    QtiMultipleSelectQuestionDTO,
    QtiMatrixQuestionDTO,
    QtiDragAndDropQuestionDTO,
} from '@/src/lib/questionRenderer/models';
import type { StudentExamViewLoadDTO } from '@/src/pages/studentExamView/viewQuestions/models/StudentExamViewDTOs';
import { ErrorMessage, TTSButton } from '@/src/ui';
import { Constants } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

interface UserResponseObject {
    [key: number]: string;
}
interface UserInteractionMetaObject {
    [key: number]: UserInteractionMetaDTO;
}
interface QuestionWithNumberDTO {
    trueQuestionNumber: number;
    question: QtiQuestionDTO;
}

interface StimulusRendererProps {
    question: QtiStimulusQuestionDTO;
    handleUpdateUserResponse?: (response: string, isFirstLoad: boolean) => void;
    setQuestionLoading: (loading: boolean) => void;
    childQuestionIds: number[] | null;
    loadedUserResponse?: string;
    options?: QuestionRendererOptions;
}

export default function StimulusRenderer({
    question,
    handleUpdateUserResponse = () => {},
    setQuestionLoading,
    childQuestionIds,
    loadedUserResponse,
    options = defaultOptions,
}: StimulusRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        showAudioButtons = false,
        language = 'en',
    } = options;

    // In case we want to limit what the chlidren questions see
    const optionsToPass = {
        disabled,
        hidePrompt,
        showAudioButtons,
        language,
    };

    const [error, setError] = useState<string>('');

    const [questionList, setQuestionList] = useState<QuestionWithNumberDTO[]>([]);
    const [userResponseObject, setUserResponseObject] = useState<UserResponseObject>({});
    const [userInteractionMetaObject, setUserInteractionMetaObject] = useState<{
        [key: number]: UserInteractionMetaDTO;
    }>({});

    const { attemptId = '0' } = useParams();

    useEffect(() => {
        (async function init() {
            setQuestionLoading(true);
            setError('');

            if (loadedUserResponse) {
                const parsed = JSON.parse(loadedUserResponse);
                if (parsed.selectedChoiceId) {
                    setUserResponseObject(parsed.selectedChoiceId);
                }
            }

            if (!childQuestionIds || childQuestionIds.length === 0) {
                return;
            }

            const newQuestionList: QuestionWithNumberDTO[] = [];
            const newUserResponseObject: UserResponseObject = { ...userResponseObject };
            const newUserInteractionMetaObject: UserInteractionMetaObject = {
                ...userInteractionMetaObject,
            };
            await Promise.all(
                childQuestionIds.map(async (id) => {
                    try {
                        const questionRes = await apiRequest<QtiQuestionDTO>(
                            'get',
                            `assessments/studentExamView/question?attemptId=${attemptId}&attemptQuestionId=${id}`
                        );
                        const attemptQuestionRes = await apiRequest<StudentExamViewLoadDTO>(
                            'get',
                            `assessments/studentExamView/studentExamAttemptQuestion?attemptId=${attemptId}&attemptQuestionId=${id}`
                        );
                        const attemptQuestionData = attemptQuestionRes.data || {};
                        newUserResponseObject[id] =
                            attemptQuestionData.attemptQuestionDetails.submittedAnswer || '';
                        newUserInteractionMetaObject[id] =
                            attemptQuestionData.attemptQuestionDetails.userInteractionMeta;

                        const parsedQuestion = questionRes.data || {};
                        if (!parsedQuestion.meta?.questionTypeMasterId) {
                            setError('Failed to load child question.');
                            return;
                        }
                        parsedQuestion.id = id;
                        const newQuestionWithNumber: QuestionWithNumberDTO = {
                            trueQuestionNumber:
                                attemptQuestionData.attemptQuestionDetails.trueQuestionNumber || 0,
                            question: parsedQuestion,
                        };
                        newQuestionList.push(newQuestionWithNumber);
                    } catch {
                        setError('Failed to load child question.');
                        return;
                    }
                })
            ).then(() => {
                setQuestionList(newQuestionList);
                setUserResponseObject(newUserResponseObject);
                setUserInteractionMetaObject(newUserInteractionMetaObject);
                setQuestionLoading(false);
            });
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stimHandleUserResponse = useCallback(
        (response: string, didUserInteract: boolean, attemptQuestionId: number) => {
            const newUserResponseObject = { ...userResponseObject };
            newUserResponseObject[attemptQuestionId] = response;
            setUserResponseObject(newUserResponseObject);
            handleUpdateUserResponse(JSON.stringify(newUserResponseObject), didUserInteract);
        },
        [handleUpdateUserResponse, userResponseObject]
    );

    function renderSingleQuestionPreview(q: QtiQuestionDTO) {
        const userResponseForThisQuestion = userResponseObject[q.id!] || undefined;
        const userInteractionMetaForThisQuestion = userInteractionMetaObject[q.id!] || undefined;
        // q.id in this case is the attempt ID, not the actual question ID. This was the easiest workaround
        switch (q.meta.questionTypeMasterId) {
            case qtm.MULTIPLE_CHOICE:
                return (
                    <MultipleChoiceRenderer
                        question={q as QtiMultipleChoiceQuestionDTO}
                        loadedUserResponse={userResponseForThisQuestion}
                        loadedUserInteractionMeta={userInteractionMetaForThisQuestion}
                        setQuestionLoading={setQuestionLoading}
                        handleUpdateUserResponse={(res, dui) =>
                            stimHandleUserResponse(res, dui, q.id!)
                        }
                        options={optionsToPass}
                    />
                );
            case qtm.OPEN_TEXT:
                return (
                    <OpenTextRenderer
                        question={q as QtiOpenTextQuestionDTO}
                        loadedUserResponse={userResponseForThisQuestion}
                        handleUpdateUserResponse={(res, dui) =>
                            stimHandleUserResponse(res, dui, q.id!)
                        }
                        options={optionsToPass}
                    />
                );
            case qtm.MULTIPLE_SELECT:
                return (
                    <MultipleSelectRenderer
                        question={q as QtiMultipleSelectQuestionDTO}
                        loadedUserResponse={userResponseForThisQuestion}
                        loadedUserInteractionMeta={userInteractionMetaForThisQuestion}
                        setQuestionLoading={setQuestionLoading}
                        handleUpdateUserResponse={(res, dui) =>
                            stimHandleUserResponse(res, dui, q.id!)
                        }
                        options={optionsToPass}
                    />
                );
            case qtm.MATRIX:
                return (
                    <MatrixRenderer
                        question={q as QtiMatrixQuestionDTO}
                        loadedUserResponse={userResponseForThisQuestion}
                        handleUpdateUserResponse={(res, dui) =>
                            stimHandleUserResponse(res, dui, q.id!)
                        }
                        options={optionsToPass}
                    />
                );
            case qtm.DRAG_AND_DROP:
                return (
                    <DragAndDropRenderer
                        question={q as QtiDragAndDropQuestionDTO}
                        loadedUserResponse={userResponseForThisQuestion}
                        handleUpdateUserResponse={(res, dui) =>
                            stimHandleUserResponse(res, dui, q.id!)
                        }
                        options={optionsToPass}
                    />
                );
            default:
                return (
                    <ErrorMessage
                        error={`Unsupported question type ${q.meta.questionTypeMasterId}`}
                    />
                );
        }
    }

    if (!question) return 'Unable to load model';
    return (
        <>
            <ErrorMessage error={error} />

            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: language,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={language === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={language === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}
            {questionList
                .sort((a, b) => a.question.id! - b.question.id!)
                .map((q) => {
                    const seaq = q.question;
                    return (
                        <div key={`stim-${seaq.uuid}`}>
                            <hr className="my-4" />
                            <div className="assessment-question-label w-fit my-2 text-center">
                                {q.trueQuestionNumber}
                            </div>
                            {renderSingleQuestionPreview(seaq)}
                        </div>
                    );
                })}
        </>
    );
}
